import React from 'react';
import {
    Checkbox,
    DefaultButton,
    Image,
    ImageFit,
    PanelType,
    PrimaryButton,
    Shimmer,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import PanelWrapper from '../Common/PanelWrapper';
import {IWorkspaceItem, IWorkspaceItemBundleSelection} from '../../models/Workspace';
import {IProductBundleItem} from '../../models/ProductBundles';
import {ISavedSearchRequest} from '../../models/Search';
import ProductBundleItemCostDisplay from '../ProductBundles/ProductBundleItemCostDisplay';
import {INormalizedProduct} from '../../models/NormalizedProduct';
import {useGetMultipleItemDetailsQuery, useGetSearchResultsQuery, useGetWorkspaceQuery} from '../../store/Api';
import {
    useGetProductBundleItemsQuery,
    useGetProductBundleItemPricesQuery,
    useGetProductBundleQuery,
    useEstimateProductBundlePriceCostMutation,
} from '../../store/endpoints/ProductBundleEndpoints';
import {useGetSavedSearchQuery} from '../../store/endpoints/SavedSearchEndpoints';
import {useUpdateWorkspaceItemPreparedConfigurationMutation} from '../../store/endpoints/WorkspaceItemsEndpoints';

interface IWorkspaceBundleConfigPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
    item?: IWorkspaceItem;
}

// Optionality semantics: "Required"/null/empty => required, "Optional" => optional, anything else => a group id.
const OPTIONAL = 'Optional';
const REQUIRED = 'Required';
const isGrouped = (item: IProductBundleItem) => !!item.details?.optionality && item.details.optionality !== OPTIONAL && item.details.optionality !== REQUIRED;
const isOptional = (item: IProductBundleItem) => item.details?.optionality === OPTIONAL;
const isRequired = (item: IProductBundleItem) => !item.details?.optionality || item.details.optionality === REQUIRED;

const formatPrice = (value: number) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

// Shared table styling — ac-main web has no `bundle-table` stylesheet class (that lives in
// ac-storefront-web), so the storefront look is reproduced with inline styles here.
const tableStyle: React.CSSProperties = {width: '100%', borderCollapse: 'collapse'};
const thStyle: React.CSSProperties = {textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '1px solid #e0e0e0', padding: '4px 6px'};
const tdStyle: React.CSSProperties = {borderBottom: '1px solid #f0f0f0', padding: '6px', verticalAlign: 'middle'};
const groupHeaderStyle: React.CSSProperties = {marginBottom: '0.5em', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.25em'};

/**
 * Editable bundle-configuration panel for a workspace bundle item, opened from the pencil icon on the
 * workspace row. Mirrors the ac-storefront-web cart bundle edit panel (BundleOptionsTables): grouped
 * items are radio choices, optionals are checkboxes, quantities are editable where the merchant enabled
 * changeQuantity, and each row shows its per-item price. The bundle total is server-driven via a
 * debounced estimate call, and Save persists the selection onto the workspace item so it flows through
 * the prepared cart path as the bundle's configuration.
 */
const WorkspaceBundleConfigPanel: React.FC<IWorkspaceBundleConfigPanelProps> = (props) => {
    const bundleId = props.item?.sourceEntityId ?? '';
    const skip = !props.isOpen || bundleId === '';

    // Configure against the current (live) bundle definition, not the order-time snapshot.
    const bundleItems = useGetProductBundleItemsQuery(bundleId, {skip});
    const bundle = useGetProductBundleQuery(bundleId, {skip});

    const [estimateBundlePrice] = useEstimateProductBundlePriceCostMutation();
    const [savePreparedConfiguration, saveState] = useUpdateWorkspaceItemPreparedConfigurationMutation();

    // Workspace default markup is the fallback the server applies when an item (and the bundle) carry
    // no markup — same source the workspace product table uses (PriceEntry / MarkupEntry).
    const workspace = useGetWorkspaceQuery(props.item?.workspace ?? '', {skip: skip || !props.item?.workspace});
    const defaultMarkup = workspace.data?.defaultMarkup;
    // The storefront this workspace came from, where it came from a storefront order: it carries the
    // storefront's markup and this bundle's markup for that storefront, two rungs of the hierarchy.
    const storefrontId = workspace.data?.storefrontId;

    // Per-item cost/price is calculated server-side (bundle default markup, or the workspace markup as
    // fallback) so every row shows a real sell price regardless of the current selection.
    const itemPrices = useGetProductBundleItemPricesQuery({bundleId, markup: defaultMarkup, storefrontId}, {skip});
    const unitPriceByProductId = React.useMemo(
        () => new Map((itemPrices.data?.items ?? []).map((p) => [p.productId, p.unitPrice])),
        [itemPrices.data]
    );

    const items: IProductBundleItem[] = React.useMemo(() => bundleItems.data?.items ?? [], [bundleItems.data]);
    const groups = React.useMemo(() => bundle.data?.details?.productGroups ?? [], [bundle.data]);

    // A bundle may carry a single saved-search item (pinned, always required, not editable here). It is
    // shown as its own read-only row rather than mixed into the selectable required/optional/group items.
    const savedSearchItem = React.useMemo(() => items.find((i) => !!i.savedSearchId), [items]);
    const savedSearch = useGetSavedSearchQuery(
        {id: savedSearchItem?.savedSearchId ?? ''},
        {skip: skip || !savedSearchItem?.savedSearchId}
    );
    // Resolve the saved search to its current top product so the row reflects what it points at now.
    const savedSearchCriteria = savedSearch.data?.criteria;
    const savedSearchResults = useGetSearchResultsQuery(
        {catalog: savedSearchCriteria?.catalog, criteria: (savedSearchCriteria ?? {}) as ISavedSearchRequest},
        {skip: skip || !savedSearchCriteria}
    );
    const savedSearchProduct = savedSearchResults.data?.products?.[0];

    const bundleQuantity = props.item?.details?.quantity ?? 1;

    // Fetch live product details for names / part numbers / images. Falls back to the source item ref.
    // Skip the saved-search item (no productId) when deriving the catalog.
    const catalog = items.find((i) => !!i.productId)?.catalog ?? props.item?.catalog ?? '';
    const productIds = React.useMemo(() => items.map((i) => i.productId).filter((id): id is string => !!id), [items]);
    const productDetails = useGetMultipleItemDetailsQuery(
        {catalog, ids: productIds},
        {skip: skip || catalog === '' || productIds.length === 0}
    );
    const productsById = React.useMemo(
        () => new Map((productDetails.data ?? []).map((p) => [p.id, p])),
        [productDetails.data]
    );

    // Selection state, seeded from the item's existing bundleSelections or the bundle defaults.
    const [selectedGroupProducts, setSelectedGroupProducts] = React.useState<Record<string, string>>({});
    const [optionalSelections, setOptionalSelections] = React.useState<Record<string, boolean>>({});
    const [itemQuantities, setItemQuantities] = React.useState<Record<string, number>>({});
    const [itemTmpQuantities, setItemTmpQuantities] = React.useState<Record<string, string>>({});
    const [estimatedPrice, setEstimatedPrice] = React.useState<number | null>(null);
    const [isEstimating, setIsEstimating] = React.useState(false);

    const initializedRef = React.useRef(false);
    const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    // (Re)initialize whenever the panel opens for a new item / the bundle loads.
    React.useEffect(() => {
        if (skip || items.length === 0) {
            initializedRef.current = false;
            return;
        }
        // Seed from the sales rep's saved prepared configuration (product id + chosen quantity).
        // Absent = bundle defaults.
        const seed = props.item?.preparedConfiguration ?? undefined;
        const seededOf = (productId: string) => seed?.find((s) => s.productId === productId);

        const groupSelections: Record<string, string> = {};
        groups.forEach((group) => {
            const groupProducts = items
                .filter((i) => i.details?.optionality === group.id)
                .sort((a, b) => (b.details?.isDefault ? 1 : 0) - (a.details?.isDefault ? 1 : 0));
            const seeded = seed ? groupProducts.find((p) => !!p.productId && !!seededOf(p.productId)) : undefined;
            const chosen = seeded ?? groupProducts[0];
            if (chosen?.productId && group.id) groupSelections[group.id] = chosen.productId;
        });
        setSelectedGroupProducts(groupSelections);

        const optionals: Record<string, boolean> = {};
        items.filter(isOptional).forEach((i) => {
            if (i.productId) optionals[i.productId] = seed ? !!seededOf(i.productId) : false;
        });
        setOptionalSelections(optionals);

        // Seed each item's quantity from the saved prepared selection where present, else the bundle default.
        const quantities: Record<string, number> = {};
        const tmpQuantities: Record<string, string> = {};
        items.forEach((i) => {
            if (!i.productId) return;
            const qty = seededOf(i.productId)?.quantity ?? i.details?.quantity ?? 1;
            quantities[i.productId] = qty;
            tmpQuantities[i.productId] = qty.toString();
        });
        setItemQuantities(quantities);
        setItemTmpQuantities(tmpQuantities);

        initializedRef.current = true;
    }, [skip, items, groups, props.item?.id]);

    // Build the flat selection list: grouped choices carry their groupId, non-grouped items (required
    // always, optional only when checked) carry no groupId. Mirrors ApplyBundleSelection on the API.
    const selection = React.useMemo((): IWorkspaceItemBundleSelection[] => {
        const result: IWorkspaceItemBundleSelection[] = [];
        groups.forEach((group) => {
            const pid = group.id ? selectedGroupProducts[group.id] : undefined;
            if (!pid) return;
            result.push({productId: pid, quantity: itemQuantities[pid] ?? 1, groupId: group.id});
        });
        items.forEach((i) => {
            if (isGrouped(i) || !i.productId) return;
            const included = isOptional(i) ? (optionalSelections[i.productId] ?? false) : true;
            if (included) result.push({productId: i.productId, quantity: itemQuantities[i.productId] ?? 1, groupId: undefined});
        });
        return result;
    }, [groups, items, selectedGroupProducts, optionalSelections, itemQuantities]);

    // Debounced server-driven price estimate for the current configuration.
    React.useEffect(() => {
        if (skip || !initializedRef.current) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setIsEstimating(true);
        debounceRef.current = setTimeout(async () => {
            try {
                // The workspace markup goes with the total exactly as it goes with the per-row prices above;
                // without it the total came back at cost while every row beside it showed a marked-up price.
                const result = await estimateBundlePrice({bundleId, request: {selection, quantity: bundleQuantity, markup: defaultMarkup, storefrontId}}).unwrap();
                setEstimatedPrice(result.price ?? null);
            } catch {
                // Leave the last good price in place on error rather than blanking the UI.
            } finally {
                setIsEstimating(false);
            }
        }, 300);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [skip, selection, bundleQuantity, defaultMarkup, storefrontId]);

    const isLoading = bundleItems.isLoading || bundle.isLoading || productDetails.isLoading;

    // Per-item quantity editing (products with changeQuantity): keep digits while typing, commit min-1 on blur.
    const trySetTmpQuantity = (productId: string, value: string) => {
        setItemTmpQuantities((prev) => ({...prev, [productId]: value.replace(/[^0-9]/g, '')}));
    };
    const blurQuantity = (productId: string) => {
        let parsed = parseInt(itemTmpQuantities[productId] ?? '', 10);
        if (isNaN(parsed) || parsed < 1) parsed = 1;
        setItemQuantities((prev) => ({...prev, [productId]: parsed}));
        setItemTmpQuantities((prev) => ({...prev, [productId]: parsed.toString()}));
    };

    const save = async () => {
        if (!props.item?.workspace || !props.item?.id) return;
        // Persist the selected products with their chosen quantities as the rep's prepared configuration.
        const configuration = selection.filter((s) => !!s.productId);
        await savePreparedConfiguration({workspaceId: props.item.workspace, wsItemId: props.item.id, configuration}).unwrap().catch(() => undefined);
        props.onDismiss();
    };

    // One table row: the include control (radio / checkbox), image, name + MPN, quantity, cost, price.
    const renderRow = (item: IProductBundleItem, kind: 'group' | 'required' | 'optional', groupId?: string) => {
        const productId = item.productId ?? '';
        const product: INormalizedProduct | undefined = productId ? productsById.get(productId) : undefined;
        const name = product?.name ?? item.sourceItem?.name ?? productId ?? 'Unknown product';
        const partNumber = product?.manufacturerPartNumber ?? item.sourceItem?.manufacturerPartNumber;
        const image = product?.image ?? item.sourceItem?.imageUrl;
        const canChangeQty = item.details?.changeQuantity === true;
        const price = unitPriceByProductId.get(productId);
        const isReq = kind === 'required';

        return (
            <tr key={`${kind}-${groupId ?? ''}-${productId}`} style={isReq ? undefined : {fontStyle: kind === 'optional' ? 'italic' : undefined}}>
                <td style={{...tdStyle, textAlign: 'center', width: 40}}>
                    {kind === 'group' &&
                        <input
                            type="radio"
                            name={`group-${groupId}`}
                            checked={groupId ? selectedGroupProducts[groupId] === productId : false}
                            onChange={() => groupId && productId && setSelectedGroupProducts((prev) => ({...prev, [groupId]: productId}))}
                            style={{width: 18, height: 18, cursor: 'pointer'}}
                        />}
                    {kind === 'optional' &&
                        <Checkbox
                            checked={optionalSelections[productId] ?? false}
                            onChange={(_, c) => productId && setOptionalSelections((prev) => ({...prev, [productId]: !!c}))}
                        />}
                    {kind === 'required' && <Checkbox checked disabled />}
                </td>
                <td style={{...tdStyle, width: 72}}>
                    <Image src={image ?? undefined} width={60} height={60} imageFit={ImageFit.contain} alt={name} />
                </td>
                <td style={tdStyle}>
                    <Text variant="mediumPlus" styles={{root: {fontWeight: 600, display: 'block'}}}>{name}</Text>
                    {partNumber && <Text variant="small" styles={{root: {color: '#666'}}}>MPN: {partNumber}</Text>}
                </td>
                <td style={{...tdStyle, textAlign: 'center', width: 90}}>
                    {canChangeQty
                        ? <TextField
                            value={itemTmpQuantities[productId] ?? (item.details?.quantity ?? 1).toString()}
                            onChange={(_, v) => trySetTmpQuantity(productId, v ?? '')}
                            onBlur={() => blurQuantity(productId)}
                            styles={{root: {width: 70, margin: '0 auto'}, field: {textAlign: 'right'}}}
                        />
                        : <span>{itemQuantities[productId] ?? item.details?.quantity ?? 1}</span>}
                </td>
                <td style={{...tdStyle, textAlign: 'right', width: 110}}>
                    <ProductBundleItemCostDisplay item={item} />
                </td>
                <td style={{...tdStyle, textAlign: 'right', width: 90}}>
                    {price != null ? formatPrice(price) : (itemPrices.isFetching ? '…' : 'N/A')}
                </td>
            </tr>
        );
    };

    const renderTable = (rows: React.ReactNode) => (
        <table style={tableStyle}>
            <thead>
                <tr>
                    <th style={{...thStyle, width: 40}} />
                    <th style={{...thStyle, width: 72}} />
                    <th style={thStyle}>Product</th>
                    <th style={{...thStyle, width: 90, textAlign: 'center'}}>Quantity</th>
                    <th style={{...thStyle, width: 110, textAlign: 'right'}}>Cost</th>
                    <th style={{...thStyle, width: 90, textAlign: 'right'}}>Price</th>
                </tr>
            </thead>
            <tbody>{rows}</tbody>
        </table>
    );

    // The saved-search row: a read-only row headed by the saved search's name. It is always included and
    // has no selection/quantity controls (the merchant configures the search; the rep can't edit it here).
    const renderSavedSearchSection = () => {
        if (!savedSearchItem) return null;
        const name = savedSearch.data?.name ?? 'Saved Search';
        const matchName = savedSearchProduct?.name ?? savedSearchItem.sourceItem?.name;
        const image = savedSearchProduct?.image ?? savedSearchItem.sourceItem?.imageUrl;
        const partNumber = savedSearchProduct?.manufacturerPartNumber ?? savedSearchItem.sourceItem?.manufacturerPartNumber;
        const quantity = savedSearchItem.details?.quantity ?? 1;
        const costItem: IProductBundleItem = {...savedSearchItem, vendors: savedSearchProduct?.vendors};
        const isResolving = savedSearch.isLoading || savedSearchResults.isFetching;
        return (
            <div style={{marginBottom: '1em'}}>
                <h3 style={groupHeaderStyle}>{savedSearch.isLoading ? 'Saved Search' : name}</h3>
                {renderTable(
                    <tr>
                        <td style={{...tdStyle, textAlign: 'center', width: 40}}>
                            <Checkbox checked disabled />
                        </td>
                        <td style={{...tdStyle, width: 72}}>
                            <Image src={image ?? undefined} width={60} height={60} imageFit={ImageFit.contain} alt={name} />
                        </td>
                        <td style={tdStyle}>
                            <Text variant="mediumPlus" styles={{root: {fontWeight: 600, display: 'block'}}}>{name}</Text>
                            {isResolving
                                ? <Spinner size={SpinnerSize.small} styles={{root: {justifyContent: 'flex-start'}}} />
                                : <Text variant="small" styles={{root: {color: '#666'}}}>
                                    {matchName ? `Current match: ${matchName}` : 'No matching product'}
                                    {partNumber ? ` · MPN: ${partNumber}` : ''}
                                </Text>}
                        </td>
                        <td style={{...tdStyle, textAlign: 'center', width: 90}}>
                            <span>{quantity}</span>
                        </td>
                        <td style={{...tdStyle, textAlign: 'right', width: 110}}>
                            <ProductBundleItemCostDisplay item={costItem} />
                        </td>
                        <td style={{...tdStyle, textAlign: 'right', width: 90}}>
                            <span>—</span>
                        </td>
                    </tr>
                )}
            </div>
        );
    };

    const bySort = React.useMemo(() => [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)), [items]);
    // The saved-search item is rendered separately (pinned, read-only), so keep it out of the editable lists.
    const requiredItems = bySort.filter((i) => isRequired(i) && !i.savedSearchId);
    const optionalItems = bySort.filter(isOptional);
    const hasGroups = groups.length > 0;

    return (
        <PanelWrapper
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            title={props.item?.adhoc?.name ?? bundle.data?.name ?? 'Configure Bundle'}
            type={PanelType.large}
            isFooterAtBottom
            footerTop={
                (estimatedPrice !== null || isEstimating)
                    ? <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '0.75em 24px', borderTop: '2px solid #e0e0e0', fontWeight: 'bold', fontSize: '1.05em'}}>
                        <span style={{marginRight: '1.5em'}}>Bundle Total:</span>
                        {isEstimating ? <Spinner size={SpinnerSize.small} /> : estimatedPrice !== null && <span>{formatPrice(estimatedPrice)}</span>}
                    </div>
                    : undefined
            }
            footerButtons={[
                <PrimaryButton key="save" text="Save" onClick={save} disabled={saveState.isLoading || isLoading} />,
                <DefaultButton key="cancel" text="Cancel" onClick={props.onDismiss} disabled={saveState.isLoading} />,
            ]}
        >
            <Stack tokens={{childrenGap: 12}} styles={{root: {padding: '8px 4px'}}}>
                <Text variant="small" styles={{root: {color: '#666'}}}>
                    Choose the options for this bundle. These selections are saved as the bundle&apos;s configuration on the prepared cart.
                </Text>
                <Text variant="mediumPlus" styles={{root: {fontWeight: 600}}}>Quantity: {bundleQuantity}</Text>

                {isLoading && <Shimmer />}

                {!isLoading && renderSavedSearchSection()}

                {!isLoading && hasGroups && groups.map((group) => {
                    // Order rows by the group's own product list so the default keeps its configured position.
                    const groupProducts = (group.products ?? [])
                        .map((pid) => items.find((i) => i.productId === pid && i.details?.optionality === group.id))
                        .filter((i): i is IProductBundleItem => !!i);
                    if (groupProducts.length === 0) return null;
                    return (
                        <div key={group.id} style={{marginBottom: '1em'}}>
                            <h3 style={groupHeaderStyle}>{group.name}</h3>
                            {renderTable(groupProducts.map((i) => renderRow(i, 'group', group.id)))}
                        </div>
                    );
                })}

                {!isLoading && (requiredItems.length > 0 || optionalItems.length > 0) && (
                    <div>
                        {hasGroups && <h3 style={groupHeaderStyle}>Additional Items</h3>}
                        {renderTable(
                            <>
                                {requiredItems.map((i) => renderRow(i, 'required'))}
                                {optionalItems.map((i) => renderRow(i, 'optional'))}
                            </>
                        )}
                    </div>
                )}

                {!isLoading && items.length === 0 && <Text>This bundle has no items.</Text>}
            </Stack>
        </PanelWrapper>
    );
};

export default WorkspaceBundleConfigPanel;
